import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import ProjectModal from './components/ProjectModal';

export interface Project {
  id: string;
  client_id: string | null;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  due_date: string | null;
  end_date: string | null;
  owner_id: string | null;
  scrum_active: boolean;
  total_value: number | null;
  estimated_hours: number | null;
  methodology: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  _client_name?: string;
  _owner_name?: string;
  _member_count?: number;
}

export interface Client { id: string; name: string; }
export interface OwnerProfile { id: string; full_name: string; email: string; }

const STATUS_OPTIONS = [
  { value: 'planejamento', label: 'Planejamento', color: 'bg-slate-100 text-slate-600' },
  { value: 'ativo', label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'pausado', label: 'Pausado', color: 'bg-orange-100 text-orange-700' },
  { value: 'concluido', label: 'Concluído', color: 'bg-sky-100 text-sky-700' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-700' },
];

export function getStatusStyle(status: string) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

export default function AdminProjetosPage() {
  const { profile } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [owners, setOwners] = useState<OwnerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [preselectedClientId, setPreselectedClientId] = useState<string | null>(null);

  // Abrir modal de novo projeto se vier de /admin/clientes/:id
  useEffect(() => {
    const clientId = searchParams.get('client_id');
    if (clientId) {
      setPreselectedClientId(clientId);
      setEditingProject(null);
      setModalOpen(true);
    }
  }, [searchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [projectsRes, clientsRes, ownersRes] = await Promise.all([
      supabase.from('projects').select('*').order('created_at', { ascending: false }),
      supabase.from('clients').select('id, name').eq('is_active', true).order('name'),
      supabase.from('profiles').select('id, full_name, email').eq('role', 'admin').eq('is_active', true).order('full_name'),
    ]);

    const projectData = projectsRes.data || [];
    setClients(clientsRes.data || []);
    setOwners(ownersRes.data || []);

    // Enriquecer com nomes
    const clientMap: Record<string, string> = {};
    (clientsRes.data || []).forEach((c) => { clientMap[c.id] = c.name; });
    const ownerMap: Record<string, string> = {};
    (ownersRes.data || []).forEach((o) => { ownerMap[o.id] = o.full_name; });

    // Contar membros
    const projectIds = projectData.map((p) => p.id);
    const membersRes = projectIds.length > 0
      ? await supabase.from('project_members').select('project_id').in('project_id', projectIds)
      : { data: [] };
    const memberCounts: Record<string, number> = {};
    (membersRes.data || []).forEach((m) => {
      memberCounts[m.project_id] = (memberCounts[m.project_id] || 0) + 1;
    });

    setProjects(projectData.map((p) => ({
      ...p,
      _client_name: clientMap[p.client_id] || '—',
      _owner_name: ownerMap[p.owner_id] || '—',
      _member_count: memberCounts[p.id] || 0,
    })));
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || (p._client_name || '').toLowerCase().includes(q);
    const matchStatus = !filterStatus || p.status === filterStatus;
    const matchClient = !filterClient || p.client_id === filterClient;
    return matchSearch && matchStatus && matchClient;
  });

  const handleSave = async (data: Partial<Project>) => {
    if (editingProject) {
      const { error } = await supabase.from('projects').update({ ...data, updated_at: new Date().toISOString() }).eq('id', editingProject.id);
      if (!error) { await logAudit('update', 'projects', editingProject.id, { name: data.name }); loadData(); }
    } else {
      const { data: inserted, error } = await supabase.from('projects').insert({ ...data, created_by: profile?.id }).select().maybeSingle();
      if (!error && inserted) { await logAudit('insert', 'projects', inserted.id, { name: data.name }); loadData(); }
    }
    setModalOpen(false);
    setEditingProject(null);
    setPreselectedClientId(null);
  };

  const openNew = () => { setEditingProject(null); setPreselectedClientId(null); setModalOpen(true); };
  const openEdit = (p: Project) => { setEditingProject(p); setModalOpen(true); };

  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-slate-800 text-2xl font-bold">Projetos</h1>
          <p className="text-slate-500 text-sm mt-1">{filtered.length} projeto{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>
          Novo Projeto
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input type="text" placeholder="Buscar por nome ou cliente..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os status</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os clientes</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-folder-line text-4xl mb-3"></i>
          <p className="text-sm">Nenhum projeto encontrado</p>
          <button onClick={openNew} className="mt-4 text-cyan-500 text-sm hover:underline cursor-pointer">Criar primeiro projeto</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Projeto</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Cliente</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Responsável</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Previsão</th>
                  <th className="text-center px-4 py-3 text-slate-500 font-medium">Membros</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const st = getStatusStyle(p.status);
                  return (
                    <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-50 border border-violet-100 shrink-0">
                            <i className="ri-folder-line text-violet-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-slate-800 font-medium">{p.name}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {p.scrum_active && <span className="px-1.5 py-0 rounded text-xs bg-violet-100 text-violet-600">Scrum</span>}
                              {p.methodology && !p.scrum_active && <span className="text-slate-400 text-xs">{p.methodology}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{p._client_name}</td>
                      <td className="px-4 py-3 text-slate-600 text-sm">{p._owner_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{fmt(p.due_date)}</td>
                      <td className="px-4 py-3 text-center text-slate-700 font-medium">{p._member_count}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(p)} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer" title="Editar">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <ProjectModal
          project={editingProject}
          clients={clients}
          owners={owners}
          preselectedClientId={preselectedClientId}
          onClose={() => { setModalOpen(false); setEditingProject(null); setPreselectedClientId(null); }}
          onSave={handleSave}
        />
      )}
    </AppLayout>
  );
}
